import { Buffer } from "buffer";
import { Account } from "../accounts";
import { Asset } from "../asset";
import { BTC, GAIA, THOR, networkLabel } from "../network";
import { EncodeObject } from "../signers/cosmos/proto-signing";
import { Coin } from "../signers/cosmos/types/cosmos/base/v1beta1/coin";
import {
  MsgExecuteContract,
  MsgMigrateContract,
  MsgSudoContract,
} from "../signers/cosmos/types/cosmwasm/wasm/v1/tx";
import { MsgDeposit } from "./deposit";
import { toBase64, toUtf8 } from "@cosmjs/encoding";
import { BaseMsg, Msg, RedactedContext } from "./msg";
import { MsgRedactedBatchCalls, MsgRedactedCall } from "./private";

const defaultEncoder = <T>(x: T) => Buffer.from(JSON.stringify(x));

const canonicalFunds = (coins: readonly Coin[]): Coin[] =>
  [...coins]
    .filter((coin) => coin.amount !== "0")
    .sort((left, right) =>
      left.denom < right.denom ? -1 : left.denom > right.denom ? 1 : 0
    );

export class MsgExec<T> extends MsgDeposit implements Msg {
  constructor(
    public account: Account,
    asset: Asset,
    amount: bigint,

    private contractAddress: string,
    private msg: T,
    private encoder: (v: T) => Uint8Array = defaultEncoder
  ) {
    super(account, asset, amount);
  }

  async toCosmosTx(): Promise<{ msgs: EncodeObject[]; memo: string }> {
    if (this.account.network === THOR) {
      const denom = this.asset?.variants?.native?.denom;
      if (!denom)
        throw new Error(`Native denom for ${this.asset.asset} not provided`);

      return {
        msgs: [
          {
            typeUrl: MsgExecuteContract.typeUrl,
            value: {
              sender: this.account.address,
              contract: this.contractAddress,
              msg: this.encoder(this.msg),
              funds:
                this.amount === 0n
                  ? []
                  : [{ denom, amount: this.amount.toString() }],
            },
          },
        ],
        memo: "",
      };
    }

    return super.toCosmosTx();
  }

  toDeposit(): { amount: bigint; symbol: string } | null {
    if (this.account.network === THOR) return null;
    return super.toDeposit();
  }

  toMemo(): string {
    if (![BTC, GAIA, THOR].includes(this.asset.chain)) {
      throw new Error(
        `Exec memo support coming soon for ${networkLabel(this.asset.chain)}`
      );
    }

    const parts = [
      `x`,
      this.contractAddress,
      Buffer.from(this.encoder(this.msg)).toString("base64"),
    ];
    return parts.join(":");
  }

  toPrivate(ctx: RedactedContext): MsgRedactedCall {
    const denom = this.asset?.variants?.native?.denom ?? "";
    return new MsgRedactedCall(
      { address: ctx.publicAddress, network: THOR },
      ctx.stealth,
      this.amount.toString(),
      denom,
      this.contractAddress,
      JSON.stringify(this.msg)
    );
  }

  toPrivateV2Call() {
    if (this.account.network !== THOR) {
      throw new Error("Private Wasm execution requires a THORChain account");
    }
    const denom = this.asset?.variants?.native?.denom;
    if (this.amount > 0n && !denom) {
      throw new Error("Private Wasm funds require a native denom");
    }
    return {
      wasm: {
        execute: {
          contract_addr: this.contractAddress,
          msg: toBase64(toUtf8(JSON.stringify(this.msg))),
          funds:
            this.amount === 0n
              ? []
              : [{ denom: denom!, amount: this.amount.toString() }],
        },
      },
    };
  }

  toPrivateV2(ctx: RedactedContext): MsgRedactedCall {
    this.toPrivateV2Call();
    return this.toPrivate(ctx);
  }
}

export class MsgExecute<T> extends BaseMsg implements Msg {
  constructor(
    public account: Account,
    private coins: Coin[],
    private contractAddress: string,
    private msg: T,
    private encoder: (v: T) => Uint8Array = defaultEncoder
  ) {
    super();
  }

  async toCosmosTx(): Promise<{ msgs: EncodeObject[]; memo: string }> {
    const funds = canonicalFunds(this.coins);

    return {
      msgs: [
        {
          typeUrl: MsgExecuteContract.typeUrl,
          value: {
            sender: this.account.address,
            contract: this.contractAddress,
            msg: this.encoder(this.msg),
            funds,
          },
        },
      ],
      memo: "",
    };
  }

  toPrivateCall() {
    return {
      wasm: {
        execute: {
          contract_addr: this.contractAddress,
          msg: toBase64(toUtf8(JSON.stringify(this.msg))),
          funds: canonicalFunds(this.coins).map((coin) => ({
            denom: coin.denom,
            amount: coin.amount,
          })),
        },
      },
    };
  }

  toPrivate(ctx: RedactedContext): MsgRedactedCall | MsgRedactedBatchCalls {
    if (this.coins.length <= 1) {
      const coin = this.coins[0];
      return new MsgRedactedCall(
        { address: ctx.publicAddress, network: THOR },
        ctx.stealth,
        coin?.amount ?? "0",
        coin?.denom ?? "",
        this.contractAddress,
        JSON.stringify(this.msg)
      );
    }
    return new MsgRedactedBatchCalls(
      { address: ctx.publicAddress, network: THOR },
      ctx.stealth,
      [this.toPrivateCall()]
    );
  }

  toPrivateV2Call() {
    if (this.account.network !== THOR) {
      throw new Error("Private Wasm execution requires a THORChain account");
    }
    return this.toPrivateCall();
  }

  toPrivateV2(ctx: RedactedContext): MsgRedactedCall | MsgRedactedBatchCalls {
    this.toPrivateV2Call();
    return this.toPrivate(ctx);
  }
}

export * from "./private";

export class MsgSudo<T> extends BaseMsg implements Msg {
  constructor(
    public account: Account,
    private contractAddress: string,
    private msg: T,
    private encoder: (v: T) => Uint8Array = defaultEncoder
  ) {
    super();
  }

  async toCosmosTx(): Promise<{ msgs: EncodeObject[]; memo: string }> {
    const value: MsgSudoContract = {
      authority: this.account.address,
      contract: this.contractAddress,
      msg: this.encoder(this.msg),
    };
    return {
      msgs: [
        {
          typeUrl: MsgSudoContract.typeUrl,
          value,
        },
      ],
      memo: "",
    };
  }
}

export class MsgMigrate<T> extends BaseMsg implements Msg {
  constructor(
    public account: Account,
    private coins: Coin[],
    private contractAddress: string,
    private msg: T,
    private codeId: bigint,
    private encoder: (v: T) => Uint8Array = defaultEncoder
  ) {
    super();
  }

  async toCosmosTx(): Promise<{ msgs: EncodeObject[]; memo: string }> {
    return {
      msgs: [
        {
          typeUrl: MsgMigrateContract.typeUrl,
          value: {
            sender: this.account.address,
            contract: this.contractAddress,
            msg: this.encoder(this.msg),
            funds: this.coins,
            codeId: this.codeId,
          },
        },
      ],
      memo: "",
    };
  }
}
